const EMPTY_GROUPS = Object.freeze({
  pending: [],
  accepted: [],
  rejected: [],
  expired: [],
  cancelled: [],
  sold: [],
  other: [],
});

function getArrayPayload(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.agreements)) return payload.agreements;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

function pickFirstId(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }
    if (typeof value === "object" && "id" in value) {
      const nestedId = pickFirstId(value.id);
      if (nestedId !== null && nestedId !== undefined) return nestedId;
    }
  }
  return null;
}

function pickFirstString(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const stringValue = String(value).trim();
    if (stringValue) return stringValue;
  }
  return "";
}

function parseTerms(rawAgreement) {
  const terms = rawAgreement?.terms;
  if (terms && typeof terms === "object" && !Array.isArray(terms)) return terms;
  if (typeof terms === "string") {
    try {
      const parsed = JSON.parse(terms);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
    } catch (_) {
      return {};
    }
  }
  return {};
}

function normalizeUserName(entity) {
  const firstName = pickFirstString(entity?.user?.first_name, entity?.first_name);
  const lastName = pickFirstString(entity?.user?.last_name, entity?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim()
    || pickFirstString(entity?.name, entity?.display_name);

  return {
    firstName,
    lastName,
    fullName,
  };
}

function normalizeImageUrl(artwork) {
  const imageUrl = artwork?.image_url;
  if (imageUrl) return imageUrl;
  if (Array.isArray(artwork?.images) && artwork.images[0]?.image) return artwork.images[0].image;
  return null;
}

function idsEqual(left, right) {
  if (left === null || left === undefined) return false;
  if (right === null || right === undefined) return false;
  return String(left) === String(right);
}

function formatCommissionForForm(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  const normalized = num <= 1 ? num * 100 : num;
  return String(Number(normalized.toFixed(2)));
}

export function getAgreementStatusGroup(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized.includes("PENDING")) return "pending";
  if (normalized.includes("ACCEPT")) return "accepted";
  if (normalized.includes("REJECT")) return "rejected";
  if (normalized.includes("EXPIRED")) return "expired";
  if (normalized.includes("CANCEL")) return "cancelled";
  if (normalized.includes("SOLD")) return "sold";
  return "other";
}

export function normalizeAgreement(rawAgreement) {
  const agreement = rawAgreement || {};
  const terms = parseTerms(agreement);
  const artwork = agreement.artwork && typeof agreement.artwork === "object" ? agreement.artwork : {};
  const artist = agreement.artist && typeof agreement.artist === "object" ? agreement.artist : {};
  const host = agreement.host && typeof agreement.host === "object" ? agreement.host : {};

  const artistName = normalizeUserName(artist);
  const hostName = normalizeUserName(host);

  return {
    id: pickFirstId(agreement.id),
    status: pickFirstString(agreement.status),
    statusGroup: getAgreementStatusGroup(agreement.status),
    revision: pickFirstNumber(agreement.revision),
    updatedAt: pickFirstString(agreement.updated_at) || null,
    counterpartyDue: pickFirstString(agreement.counterparty_due) || null,
    lastProposedBy: pickFirstString(
      agreement.last_proposed_by_display,
      agreement.last_proposed_by
    ) || null,
    termsHash: pickFirstString(agreement.terms_hash) || null,

    startDate: pickFirstString(agreement.start_date, terms.start_date) || null,
    endDate: pickFirstString(agreement.end_date, terms.end_date) || null,
    artworkPrice: pickFirstNumber(
      agreement.artwork_price,
      agreement.price,
      terms.artwork_price,
      terms.price,
      artwork.price
    ),
    commissionArtist: pickFirstNumber(agreement.commission_artist, terms.commission_artist),
    commissionHost: pickFirstNumber(agreement.commission_host, terms.commission_host),
    insuranceResponsibility: pickFirstString(
      agreement.insurance_responsibility,
      terms.insurance_responsibility
    ),
    shippingToHostResponsibility: pickFirstString(
      agreement.shipping_to_host_responsibility,
      terms.shipping_to_host_responsibility
    ),
    shippingFromHostResponsibility: pickFirstString(
      agreement.shipping_from_host_responsibility,
      terms.shipping_from_host_responsibility
    ),

    artwork: {
      id: pickFirstId(artwork.id, agreement.artwork_id, agreement.artwork),
      title: pickFirstString(artwork.title) || "Untitled",
      price: pickFirstNumber(artwork.price, agreement.artwork_price, agreement.price, terms.artwork_price, terms.price),
      status: pickFirstString(artwork.status) || null,
      imageUrl: normalizeImageUrl(artwork),
      images: Array.isArray(artwork.images) ? artwork.images : [],
      raw: Object.keys(artwork).length ? artwork : null,
    },

    artist: {
      id: pickFirstId(artist.id, agreement.artist_id, agreement.artist),
      firstName: artistName.firstName,
      lastName: artistName.lastName,
      fullName: artistName.fullName || "-",
      raw: Object.keys(artist).length ? artist : null,
    },

    host: {
      id: pickFirstId(host.id, agreement.host_id, agreement.host),
      firstName: hostName.firstName,
      lastName: hostName.lastName,
      fullName: hostName.fullName || "-",
      raw: Object.keys(host).length ? host : null,
    },

    raw: agreement,
  };
}

export function normalizeAgreementList(response) {
  const agreements = getArrayPayload(response).map(normalizeAgreement);
  return {
    agreements,
    raw: response,
  };
}

export function groupAgreementsByStatus(agreements) {
  return (Array.isArray(agreements) ? agreements : []).reduce(
    (acc, agreement) => {
      const key = getAgreementStatusGroup(agreement?.status);
      acc[key] = [...acc[key], agreement];
      return acc;
    },
    {
      pending: [],
      accepted: [],
      rejected: [],
      expired: [],
      cancelled: [],
      sold: [],
      other: [],
    }
  );
}

export function buildAgreementFormTerms(agreement) {
  const normalized = normalizeAgreement(agreement?.raw || agreement);
  return {
    commission_artist: formatCommissionForForm(normalized.commissionArtist),
    commission_host: formatCommissionForForm(normalized.commissionHost),
    insurance_responsibility: normalized.insuranceResponsibility || "",
    shipping_to_host_responsibility: normalized.shippingToHostResponsibility || "",
    shipping_from_host_responsibility: normalized.shippingFromHostResponsibility || "",
    start_date: normalized.startDate || "",
    end_date: normalized.endDate || "",
    artwork_price: normalized.artworkPrice === null ? "" : String(normalized.artworkPrice),
  };
}

export function mergeAgreementUpdate(agreements, updatedAgreement) {
  const normalized = normalizeAgreement(updatedAgreement?.raw || updatedAgreement);
  if (!normalized.id) return Array.isArray(agreements) ? agreements : [];

  const current = Array.isArray(agreements) ? agreements : [];
  const index = current.findIndex((item) => idsEqual(item?.id, normalized.id));

  if (index === -1) return [normalized, ...current];

  return current.map((item, itemIndex) => (itemIndex === index ? normalized : item));
}

export function removeAgreementById(agreements, agreementId) {
  return (Array.isArray(agreements) ? agreements : []).filter(
    (agreement) => !idsEqual(agreement?.id, agreementId)
  );
}

export function replaceArtworkInList(artworks, updatedArtwork) {
  if (!updatedArtwork?.id) return Array.isArray(artworks) ? artworks : [];

  const list = Array.isArray(artworks) ? artworks : [];
  const exists = list.some((item) => idsEqual(item?.id, updatedArtwork.id));
  if (!exists) return list;

  return list.map((item) => (
    idsEqual(item?.id, updatedArtwork.id)
      ? { ...item, ...updatedArtwork }
      : item
  ));
}

export function removeHostedArtworkByArtworkId(hostedArtworks, artworkId) {
  return (Array.isArray(hostedArtworks) ? hostedArtworks : []).filter((item) => {
    const nestedId = item?.artwork?.id ?? item?.artwork_id ?? item?.artwork;
    return !idsEqual(nestedId, artworkId);
  });
}

export function getEmptyAgreementGroups() {
  return {
    pending: [...EMPTY_GROUPS.pending],
    accepted: [...EMPTY_GROUPS.accepted],
    rejected: [...EMPTY_GROUPS.rejected],
    expired: [...EMPTY_GROUPS.expired],
    cancelled: [...EMPTY_GROUPS.cancelled],
    sold: [...EMPTY_GROUPS.sold],
    other: [...EMPTY_GROUPS.other],
  };
}
